import { T } from '@territory/tokens';
import { DENSE_PHONE, TOUCH_MIN } from '../scale.js';
import { consoleRole, drawerRole } from './consoleRoles.js';

/*
 * The Users drawer v2 button (legacy #ocbDrawer .dv-btn and .dv-ib), shared by Button and IconButton
 * variant="drawer". Fill, ink and edge live in the sheet, not inline, so hover and disabled can replace them and a
 * caller `style` still wins. Colors are the --console-dv-* roles (products.consoles) with base fallbacks.
 */

/* verbatim legacy .dv-btn, off-scale */
export const DRAWER_BTN_H = 34;
export const DRAWER_BTN_H_SM = 28;
export const DRAWER_BTN_PX = 12;
export const DRAWER_BTN_PX_SM = 10;
export const DRAWER_BTN_R = 9;
export const DRAWER_BTN_GAP = 6;
export const DRAWER_BTN_FONT = 13;
export const DRAWER_BTN_FONT_SM = 12.5;
export const DRAWER_BTN_ICON = 15;
export const DRAWER_IB_ICON = 16;
const DRAWER_MS = 200;
const DISABLED_OPACITY = 0.75;
const PRI_DISABLED_OPACITY = 0.55;
const PRI_HOVER_FILTER = 'brightness(1.05)';

/* Base fallbacks: the drawer look degrades to the base surface, edge and hues outside the owner console. */
const C = {
  btn: drawerRole('btn', T.surface),
  btnH: drawerRole('btn-h', T.surfaceAlt),
  edge: drawerRole('border-s', T.separatorBold),
  ink: consoleRole('drawer-ink', T.label),
  dim: drawerRole('dim', T.labelQuad),
  em: drawerRole('em', T.accent),
  emI: drawerRole('em-i', T.accent),
  emT: drawerRole('em-t', T.accentSoft),
  emB: drawerRole('em-b', T.separatorBold),
  puI: drawerRole('pu-i', T.purple),
  puT: drawerRole('pu-t', T.purpleSoft),
  puB: drawerRole('pu-b', T.purple),
  redI: drawerRole('red-i', T.red),
  redB: drawerRole('red-b', T.red),
  priInk: drawerRole('pri-ink', T.onAccent),
  priShadow: drawerRole('pri-shadow', 'none'),
};

/* Tones: legacy .pri (primary), .tint, .dng (danger), .acc (accent); neutral is the bare .dv-btn. A tone rule has the
   same weight as the disabled rule and comes after it, so a disabled tone keeps its ink, as legacy. The general hover
   out-ranks the danger and accent tones (legacy hover turns their edge emerald too); tint and primary set their own. */
export function drawerButtonCss(sel: string): string {
  const on = `${sel}:not([aria-disabled]):hover`;
  return `${sel} { background: ${C.btn}; color: ${C.ink}; border: 1px solid ${C.edge}; transition: border-color ${DRAWER_MS}ms ${T.ease}, background ${DRAWER_MS}ms; }
${on} { border-color: ${C.emB}; background: ${C.btnH}; }
${sel}:focus-visible { outline: 2px solid ${C.em}; outline-offset: 2px; }
${sel}[aria-disabled] { color: ${C.dim}; opacity: ${DISABLED_OPACITY}; }
${sel}[data-rv-tone="primary"] { background: ${C.em}; border-color: ${C.em}; color: ${C.priInk}; box-shadow: ${C.priShadow}; }
${sel}[data-rv-tone="primary"]:not([aria-disabled]):hover { background: ${C.em}; border-color: ${C.em}; filter: ${PRI_HOVER_FILTER}; }
${sel}[data-rv-tone="primary"][aria-disabled] { color: ${C.priInk}; opacity: ${PRI_DISABLED_OPACITY}; }
${sel}[data-rv-tone="tint"] { border-color: ${C.puB}; background: ${C.puT}; color: ${C.puI}; }
${sel}[data-rv-tone="tint"]:not([aria-disabled]):hover { border-color: ${C.puI}; background: ${C.puT}; }
${sel}[data-rv-tone="danger"] { border-color: ${C.redB}; color: ${C.redI}; }
${sel}[data-rv-tone="accent"] { color: ${C.emI}; border-color: ${C.emB}; background: ${C.emT}; }
${DENSE_PHONE} { ${sel} { min-height: ${TOUCH_MIN}px; min-width: ${TOUCH_MIN}px; } }`;
}
